"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStudio } from "@/contexts/studio-context";
import { useActivePianoNotes } from "@/hooks/use-active-piano-notes";
import { useHintPublisher } from "@/hooks/use-hint-publisher";
import { chordQualityToLayout } from "@/lib/virtuosa-layout/quality-mapper";
import type { Hint } from "@/contexts/hint-context";
import { applyInversion, type ChordEntry } from "@/lib/music/chord-catalog";
import { ROOT_PITCH_CLASS } from "@/lib/music/theory-helpers";
import {
  applyResult,
  BEATS_PER_MEASURE,
  EMPTY_SCOREBOARD,
  HINT_BASE_OCTAVE,
  settleMeasure,
  toPc,
  type ChordPress,
  type ChordWindow,
  type EventType,
  type LastResult,
  type Scoreboard,
  type TempoLevel,
} from "./tempo-drill-helpers";

type UseTempoDrillArgs = {
  entry: ChordEntry;
  level: TempoLevel;
  positions: number;
  /** Pixel distance between adjacent chord slots, drives the snap-from-right
   *  animation. Lifted from the parent so the drill matches the responsive
   *  layout in the stage. */
  step: number;
};

export type TempoDrillState = {
  // Display state
  beat: number;
  posIdx: number;
  slideOffset: number;
  animating: boolean;
  combo: number;
  bestCombo: number;
  hits: number;
  misses: number;
  recentEvents: readonly EventType[];
  lastResult: LastResult | null;
  countInDone: number;

  // Derived flags
  running: boolean;
  inCountIn: boolean;
  drillActive: boolean;

  // Required-chord context for the current measure
  requiredPcs: readonly number[];
  hintMidis: readonly number[];

  // Active piano state (for visual feedback)
  activePcs: ReadonlySet<number>;

  // Actions
  togglePlay: () => void;
  reset: () => void;
};

/**
 * Latest beat received from the metronome (after the lookahead deferral).
 * `count` is the unbounded beat counter since play started:
 * - 0 : before any metronome beat (just clicked Play)
 * - 1..BEATS_PER_MEASURE : count-in beats
 * - BEATS_PER_MEASURE+1 and beyond : drill mode (the first such beat is
 *   the FIRST drill downbeat)
 */
type BeatState = {
  /** Beat index within the measure (0 = downbeat). */
  beat: number;
  count: number;
  /** `performance.now()` time of the beat's audio click. */
  realTimeMs: number;
};

const INITIAL_BEAT: BeatState = { beat: 0, count: 0, realTimeMs: 0 };

type SlideState = {
  offset: number;
  animating: boolean;
};

const SLIDE_REST: SlideState = { offset: 0, animating: true };

/**
 * Drives the tempo-drill state machine: count-in, measure settlement,
 * timing-tolerant press detection, sliding-queue animation triggers.
 *
 * Why a dedicated hook: this logic is tightly coupled to real-time audio
 * (the continuous metronome's lookahead) and reasoning about it inside the
 * presentation component made the component impossible to read. Here it
 * lives next to its helpers, with its invariants documented inline.
 */
export function useTempoDrill({
  entry,
  level,
  positions,
  step,
}: UseTempoDrillArgs) {
  const { studio } = useStudio();
  const rootPc = useMemo(() => ROOT_PITCH_CLASS[entry.root], [entry.root]);

  // ── Display state ─────────────────────────────────────────────
  const [running, setRunning] = useState(false);
  const [lastBeat, setLastBeat] = useState<BeatState>(INITIAL_BEAT);
  const [posIdx, setPosIdx] = useState(0);
  const [slide, setSlide] = useState<SlideState>(SLIDE_REST);
  const [scoreboard, setScoreboard] = useState<Scoreboard>(EMPTY_SCOREBOARD);

  // ── Refs that don't trigger re-renders ────────────────────────
  /**
   * Real-time of the current drill measure's downbeat (audio click time).
   * `null` until the first drill downbeat, which doubles as the "nothing
   * to settle yet" marker.
   */
  const measureStartTimeRef = useRef<number | null>(null);
  /**
   * Queue of fresh chord presses (timestamp + held content) not yet
   * consumed by a settlement. Content is validated at settlement, not at
   * push time: the required inversion flips exactly on the downbeat while
   * the timing tolerance straddles it. Multiple slots are needed because a
   * press that anticipates the next downbeat must be kept for the *next*
   * settlement.
   */
  const pressQueueRef = useRef<ChordPress[]>([]);
  /**
   * Pending setTimeouts that defer beat state updates until the actual audio
   * click time. The metronome listener fires up to ~100 ms BEFORE the click;
   * applying state changes immediately would advance `posIdx` ahead of what
   * the user perceives, causing presses during the perceived "old" measure
   * to be validated against the new chord.
   */
  const pendingBeatTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const inCountIn =
    running && lastBeat.count >= 1 && lastBeat.count <= BEATS_PER_MEASURE;
  const drillActive = running && lastBeat.count > BEATS_PER_MEASURE;

  // ── Subscribe to the continuous metronome ─────────────────────
  useEffect(() => {
    if (!studio) {
      return;
    }

    const cm = studio.continuousMetronome;

    if (!running) {
      cm.stop();
      for (const id of pendingBeatTimersRef.current) {
        clearTimeout(id);
      }

      pendingBeatTimersRef.current = [];

      return;
    }

    cm.start(level.bpm, BEATS_PER_MEASURE);

    const off = cm.onBeat((event) => {
      // Defer state updates until the actual audio click. Without this,
      // `posIdx` advances ~100 ms before the user hears the new measure
      // start, so a press they make during the perceived old measure is
      // evaluated against the *new* chord requirement (false validation).
      const delay = Math.max(0, event.realTimeMs - performance.now());
      const timerId = setTimeout(() => {
        setLastBeat((prev) => ({
          beat: event.beat,
          count: prev.count + 1,
          realTimeMs: event.realTimeMs,
        }));
        pendingBeatTimersRef.current = pendingBeatTimersRef.current.filter(
          (id) => id !== timerId,
        );
      }, delay);
      pendingBeatTimersRef.current.push(timerId);
    });

    return () => {
      off();
      cm.stop();
      for (const id of pendingBeatTimersRef.current) {
        clearTimeout(id);
      }

      pendingBeatTimersRef.current = [];
    };
  }, [studio, running, level.bpm]);

  // ── Compute the chord window around the current position ──────
  const chordWindow = useMemo<ChordWindow>(() => {
    const pcsFor = (idx: number) =>
      applyInversion(entry.intervals, idx).map((s) => toPc(rootPc + s));
    const prevIdx = (posIdx - 1 + positions) % positions;

    return { incoming: pcsFor(posIdx), outgoing: pcsFor(prevIdx) };
  }, [entry.intervals, posIdx, positions, rootPc]);

  const requiredPcs = chordWindow.incoming;

  const hintMidis = useMemo(
    () =>
      applyInversion(entry.intervals, posIdx).map((s) => {
        const baseMidi = (HINT_BASE_OCTAVE + 1) * 12 + rootPc;

        return baseMidi + s;
      }),
    [entry.intervals, posIdx, rootPc],
  );

  /**
   * Chord window read by the settlement effect through a ref (not via
   * deps) so it doesn't re-run when posIdx advances; at settlement time
   * the ref still holds the pre-advance window, which is the one the
   * settled presses belong to.
   */
  const settleWindowRef = useRef(chordWindow);
  useEffect(() => {
    settleWindowRef.current = chordWindow;
  }, [chordWindow]);

  // ── Measure settlement ────────────────────────────────────────
  useEffect(() => {
    if (!drillActive || lastBeat.beat !== 0) {
      return;
    }

    // Use the audio-click real-time (not performance.now() at effect run
    // time) to avoid the metronome lookahead bias on timing comparisons.
    const previousMeasureStart = measureStartTimeRef.current;
    measureStartTimeRef.current = lastBeat.realTimeMs;

    // Skip the very first drill downbeat (transition from count-in): there
    // is no previous drill measure to settle yet, just start measure 1.
    // We deliberately keep the press queue so a press anticipating the
    // first downbeat is still credited at the *next* settlement.
    if (previousMeasureStart === null) {
      return;
    }

    const { result, nextQueue } = settleMeasure(
      pressQueueRef.current,
      previousMeasureStart,
      lastBeat.realTimeMs,
      settleWindowRef.current,
      Date.now(),
    );
    pressQueueRef.current = nextQueue;
    setScoreboard((board) => applyResult(board, result));

    // Update posIdx IMMEDIATELY so validation targets the new chord. Then
    // run a snap-from-right animation: position the new content at +STEP
    // without transition, then transition slideOffset back to 0.
    setSlide({ offset: step, animating: false });
    setPosIdx((p) => (p + 1) % positions);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSlide(SLIDE_REST);
      });
    });
  }, [lastBeat, drillActive, positions, step]);

  // ── Active piano state (visual feedback only) ─────────────────
  const activeMidis = useActivePianoNotes();
  const activePcs = useMemo(() => {
    const set = new Set<number>();
    for (const m of activeMidis) {
      set.add(toPc(m));
    }

    return set;
  }, [activeMidis]);

  // ── Capture chord presses straight from the piano events ──────
  // Subscribing to raw press/release events (rather than deriving from the
  // React-mirrored active notes) timestamps the press at the MIDI event,
  // free of render latency, and removes the race between a press landing
  // just after the click and the settlement advancing posIdx. A snapshot
  // is pushed on every press that adds a NEW pitch class, so holding a
  // chord across a measure boundary without re-pressing still doesn't
  // count as a fresh press.
  useEffect(() => {
    if (!studio || !running) {
      return;
    }

    const piano = studio.getPiano();
    const heldMidis = new Set<number>();

    return piano.on((event) => {
      if (event.type !== "press" && event.type !== "release") {
        return;
      }

      const midi = event.note.toMidi();

      if (event.type === "release") {
        heldMidis.delete(midi);

        return;
      }

      const pc = toPc(midi);
      let pcIsNew = true;
      let lowest = midi;

      for (const m of heldMidis) {
        if (toPc(m) === pc) {
          pcIsNew = false;
        }

        if (m < lowest) {
          lowest = m;
        }
      }

      heldMidis.add(midi);

      if (!pcIsNew) {
        return;
      }

      const pcs = new Set<number>();
      for (const m of heldMidis) {
        pcs.add(toPc(m));
      }

      pressQueueRef.current.push({
        time: performance.now(),
        pcs: [...pcs],
        bassPc: toPc(lowest),
      });
    });
  }, [studio, running]);

  // ── Publish hint into the shared HintContext ──────────────────
  const hint = useMemo<Hint | null>(() => {
    if (hintMidis.length === 0) {
      return null;
    }

    const layoutQuality = chordQualityToLayout(entry.quality);

    return {
      source: "chord-practice/tempo",
      chord:
        layoutQuality !== null
          ? { rootSemi: rootPc, quality: layoutQuality }
          : undefined,
      voicing: hintMidis,
      midis: hintMidis.map((midi) => ({ midi, color: 0x8b5cf6 })),
    };
  }, [hintMidis, entry.quality, rootPc]);
  useHintPublisher(hint);

  // ── Reset / toggle ────────────────────────────────────────────
  const clearRunState = useCallback(() => {
    measureStartTimeRef.current = null;
    pressQueueRef.current = [];
    for (const id of pendingBeatTimersRef.current) {
      clearTimeout(id);
    }

    pendingBeatTimersRef.current = [];
  }, []);

  const reset = useCallback(() => {
    setRunning(false);
    setLastBeat(INITIAL_BEAT);
    setPosIdx(0);
    setSlide(SLIDE_REST);
    setScoreboard(EMPTY_SCOREBOARD);
    clearRunState();
  }, [clearRunState]);

  const togglePlay = useCallback(() => {
    // Whether starting or pausing, re-arm count-in for next start.
    setRunning((wasRunning) => !wasRunning);
    setLastBeat(INITIAL_BEAT);
    clearRunState();
  }, [clearRunState]);

  const state: TempoDrillState = {
    beat: lastBeat.beat,
    posIdx,
    slideOffset: slide.offset,
    animating: slide.animating,
    combo: scoreboard.combo,
    bestCombo: scoreboard.bestCombo,
    hits: scoreboard.hits,
    misses: scoreboard.misses,
    recentEvents: scoreboard.recentEvents,
    lastResult: scoreboard.lastResult,
    countInDone: lastBeat.count,
    running,
    inCountIn,
    drillActive,
    requiredPcs,
    hintMidis,
    activePcs,
    togglePlay,
    reset,
  };

  return state;
}
